import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@/components/ui/Dialog';
import type { Ledger, Collection, Tag } from '@/api/types';
import { isLedgerArchived } from '@/utils/ledger';

interface LedgerHeaderProps {
    ledger: Ledger;
    collection?: Collection;
    tags: Tag[];
}

/**
 * Header component for the Ledger Detail page.
 * Displays:
 * - Ledger name as title (truncated if long)
 * - Description info icon (if description exists)
 * - Archived badge (if archived)
 * - Collection and tag badges
 */
export function LedgerHeader({
    ledger,
    collection,
    tags,
}: LedgerHeaderProps) {
    const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false);

    const isArchived = isLedgerArchived(ledger);

    return (
        <>
            <div>
                {/* Title row - single line with truncation */}
                <div className="flex min-h-[40px] items-center gap-2 min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                        {ledger.label}
                    </h1>
                    {ledger.description && (
                        <button
                            onClick={() => setIsDescriptionDialogOpen(true)}
                            className="p-1 rounded-full text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:text-primary-light dark:hover:bg-gray-800 transition-colors shrink-0"
                            aria-label="View description"
                            title="View description"
                        >
                            <InformationCircleIcon className="h-5 w-5" />
                        </button>
                    )}
                    {isArchived && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 shrink-0">
                            Archived
                        </span>
                    )}
                </div>

                {/* Badges row */}
                {(collection || tags.length > 0) && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {/* Collection badge (filled style) */}
                        {collection && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                {collection.label}
                            </span>
                        )}

                        {/* Tag badges (outlined style) */}
                        {tags.map((tag) => (
                            <span
                                key={tag.id}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-transparent border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400"
                            >
                                {tag.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Description Dialog */}
            {ledger.description && (
                <Dialog
                    open={isDescriptionDialogOpen}
                    onClose={() => setIsDescriptionDialogOpen(false)}
                    title="Description"
                    size="md"
                >
                    <div className="max-h-[60vh] overflow-y-auto">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {ledger.description}
                        </p>
                    </div>
                </Dialog>
            )}
        </>
    );
}
